import {
  IProductionDashboardBayVehicle,
  IProductionDashboardBayVehicleAssembly,
  PartAssemblyType,
} from "../models/ProductionDashboard";
import { isStationFinished } from "./awaitingNextStation";

type SubAssemblyRow = Pick<
  IProductionDashboardBayVehicleAssembly,
  "type" | "partsRemoved" | "partsTotal" | "workedSeconds" | "startedOn" | "timerFinished"
>;

/**
 * Whether a sub assembly on a vehicle is being worked, so its column should show the vehicle.
 *
 * Sub assemblies are worked off the line in parallel with it - an engine on a bench while the
 * shell carries on down the stations - so this is read from the assembly's own row, never from
 * the (main) station the vehicle is at. Underway means started (timer running, time already on
 * the clock, or a part actioned) and not finished. A finished sub assembly - every part actioned
 * with the timer stopped, or with no parts the timer finished - drops out of its column: there is
 * no next station for it to wait for.
 */
export const isSubAssemblyUnderway = (assembly: SubAssemblyRow): boolean => {
  if (assembly.type !== PartAssemblyType.Sub) return false;
  if (assembly.startedOn) return true;

  const started = (assembly.workedSeconds ?? 0) > 0 || assembly.partsRemoved > 0;
  return started && !isStationFinished(assembly);
};

/**
 * The vehicle as seen from one of its sub assemblies: the current-assembly fields swapped for
 * that assembly's own progress and timer, so a chip in a sub-assembly column shows the sub
 * assembly's timer and parts rather than the main station the vehicle is on.
 */
export const vehicleAtSubAssembly = (
  vehicle: IProductionDashboardBayVehicle,
  assembly: IProductionDashboardBayVehicleAssembly
): IProductionDashboardBayVehicle => ({
  ...vehicle,
  currentAssemblyName: assembly.name,
  currentAssemblyPartsRemoved: assembly.partsRemoved,
  currentAssemblyPartsTotal: assembly.partsTotal,
  currentAssemblyElapsedSeconds: assembly.workedSeconds,
  currentAssemblyStartedOn: assembly.startedOn,
  currentAssemblyAverageMinutes: assembly.targetMinutes,
  currentAssemblyWarningMinutes: assembly.warningMinutes,
  currentAssemblyTimerFinished: assembly.timerFinished,
});
